/**
 * Периоды «Окупаемости» (спека §5.2–5.3). День — сутки по Лос-Анджелесу:
 * в этой зоне auto-ads режет дни расхода и покупок. Все функции чистые,
 * работают со строками `YYYY-MM-DD` и не зависят от зоны браузера.
 */
export const PAYBACK_TZ = 'America/Los_Angeles';
const YMD = /^\d{4}-\d{2}-\d{2}$/;
const DAY_MS = 86_400_000;
export function isYmd(v) {
    if (typeof v !== 'string' || !YMD.test(v))
        return false;
    // `Date.parse('2026-02-30')` молча даёт 2 марта — сверяем обратным преобразованием.
    const ms = Date.parse(`${v}T00:00:00Z`);
    return !Number.isNaN(ms) && new Date(ms).toISOString().slice(0, 10) === v;
}
function toUtc(day) {
    return Date.parse(`${day}T00:00:00Z`);
}
function fromUtc(ms) {
    return new Date(ms).toISOString().slice(0, 10);
}
export function addDays(day, n) {
    return fromUtc(toUtc(day) + n * DAY_MS);
}
/** Сколько дней в периоде, оба конца включительно. */
export function spanDays(p) {
    return Math.round((toUtc(p.to) - toUtc(p.from)) / DAY_MS) + 1;
}
/** День недели: 0 — воскресенье … 6 — суббота. */
function weekday(day) {
    return new Date(toUtc(day)).getUTCDay();
}
/** Сегодня по Лос-Анджелесу. */
export function todayLa(now = new Date()) {
    const parts = new Intl.DateTimeFormat('en-CA', {
        timeZone: PAYBACK_TZ,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
    }).formatToParts(now);
    const get = (t) => parts.find((x) => x.type === t)?.value ?? '';
    return `${get('year')}-${get('month')}-${get('day')}`;
}
/**
 * Последний закрытый день: по чему auto-ads уже закрыл расход
 * (`freshness.snapshotsFinalizedThrough`), иначе вчера по LA. Не позже вчера.
 */
export function lastClosedDay(freshness, today = todayLa()) {
    const yesterday = addDays(today, -1);
    const fin = freshness?.snapshotsFinalizedThrough ?? null;
    return isYmd(fin) && fin < yesterday ? fin : yesterday;
}
/** Последняя полная неделя пн–вс, которая закончилась до `today`. */
export function lastFullWeek(today) {
    const dow = weekday(today);
    const sunday = addDays(today, -(dow === 0 ? 7 : dow));
    return { from: addDays(sunday, -6), to: sunday };
}
/** `n` дней, которые заканчиваются последним закрытым днём. */
export function lastNDays(n, closedDay) {
    return { from: addDays(closedDay, -(n - 1)), to: closedDay };
}
/** Вчера (один день). */
export function yesterdayOf(today) {
    const d = addDays(today, -1);
    return { from: d, to: d };
}
/** Предыдущий период той же длины. */
export function prevPeriod(p) {
    const n = spanDays(p);
    return { from: addDays(p.from, -n), to: addDays(p.to, -n) };
}
/** Сдвиг на длину периода (у недели — ровно на календарную неделю). */
export function shiftPeriod(p, dir) {
    const n = spanDays(p);
    return { from: addDays(p.from, dir * n), to: addDays(p.to, dir * n) };
}
/** Понедельник недели дня. */
export function mondayOf(day) {
    const dow = weekday(day);
    return addDays(day, -((dow + 6) % 7));
}
/** 12 недель: с понедельника 11 недель назад по последний закрытый день. */
export function twelveWeeks(closedDay) {
    return { from: addDays(mondayOf(closedDay), -77), to: closedDay };
}
/** 6 месяцев: с 1-го числа 5 месяцев назад по последний закрытый день. */
export function sixMonths(closedDay) {
    const [y, m] = closedDay.split('-').map(Number);
    const idx = y * 12 + (m - 1) - 5;
    const yy = Math.floor(idx / 12);
    const mm = (idx % 12) + 1;
    return { from: `${yy}-${String(mm).padStart(2, '0')}-01`, to: closedDay };
}
/** Окно уже не длиннее `maxDays`: начало подтягивается к концу. */
export function clampPeriod(p, maxDays) {
    if (p.from > p.to)
        return { from: p.to, to: p.from };
    return spanDays(p) > maxDays ? { from: addDays(p.to, -(maxDays - 1)), to: p.to } : p;
}
// --- подписи ----------------------------------------------------------------
const MONTH_SHORT = [
    'янв',
    'фев',
    'мар',
    'апр',
    'мая',
    'июн',
    'июл',
    'авг',
    'сен',
    'окт',
    'ноя',
    'дек',
];
const MONTH_FULL = [
    'Январь',
    'Февраль',
    'Март',
    'Апрель',
    'Май',
    'Июнь',
    'Июль',
    'Август',
    'Сентябрь',
    'Октябрь',
    'Ноябрь',
    'Декабрь',
];
function parts(day) {
    const [y, m, d] = day.split('-').map(Number);
    return { y, m, d };
}
/** «20 окт». */
export function dayLabel(day) {
    const { m, d } = parts(day);
    return `${d} ${MONTH_SHORT[m - 1]}`;
}
/**
 * Диапазон: «12–18 окт», «29 июн – 5 июл»; через год — «29 дек 2025 – 4 янв 2026».
 */
export function weekLabel(from, to) {
    if (from === to)
        return dayLabel(from);
    const a = parts(from);
    const b = parts(to);
    if (a.y !== b.y)
        return `${dayLabel(from)} ${a.y} – ${dayLabel(to)} ${b.y}`;
    if (a.m === b.m)
        return `${a.d}–${b.d} ${MONTH_SHORT[b.m - 1]}`;
    return `${dayLabel(from)} – ${dayLabel(to)}`;
}
/** «Июль 2026» по ключу `2026-07` (или любому дню месяца). */
export function monthLabel(key) {
    const [y, m] = key.split('-').map(Number);
    return `${MONTH_FULL[m - 1]} ${y}`;
}
/** Подпись периода: один день — «20 окт», иначе диапазон. */
export function periodLabel(p) {
    return weekLabel(p.from, p.to);
}
/**
 * «расход за 20–21 окт предварительный»; дни не подряд — через запятую.
 * Пусто — `null`.
 */
export function preliminaryLabel(days) {
    const sorted = [...new Set(days.filter(isYmd))].sort();
    if (sorted.length === 0)
        return null;
    const first = sorted[0];
    const last = sorted[sorted.length - 1];
    const contiguous = spanDays({ from: first, to: last }) === sorted.length;
    const what = contiguous ? weekLabel(first, last) : sorted.map(dayLabel).join(', ');
    return `расход за ${what} предварительный`;
}
/** «неделей раньше» для недели, иначе «периодом раньше». */
export function previousWord(p) {
    const n = spanDays(p);
    if (n === 7)
        return 'неделей раньше';
    if (n === 1)
        return 'днём раньше';
    return 'периодом раньше';
}
//# sourceMappingURL=period.js.map